'use client';

import type { Member } from '../../types/member';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Carousel } from '../ui/carousel';
import { MemberCard } from './member-card';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

interface TeamMembersProps {
  className?: {
    section?: string;
    container?: string;
  };
  members: Member[];
  title: string;
  showDescription?: boolean;
  subtitle?: string;
}

export function TeamMembers({
  className,
  members,
  title,
  showDescription,
  subtitle,
}: TeamMembersProps) {
  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex, slideCount } = carouselContext;

  const progress = ((selectedIndex + 1) / slideCount) * 100;

  const { t } = useTranslations();

  return (
    <section
      className={cn('custom-section', 'gap-8 pl-4 pr-0 sm:gap-10 lg:gap-12', className?.section)}
    >
      <div className={cn('custom-container', 'flex flex-col items-center justify-center gap-4')}>
        <Heading
          title={title}
          subtitle={subtitle}
          className={{ container: 'w-full max-w-[550px] text-center' }}
        />
        {!!showDescription && (
          <p className="w-full text-center text-sm text-muted-foreground sm:text-base">
            {t('text3603')} <b>{t('text3604')}</b>
          </p>
        )}
      </div>
      <div className={cn('custom-container', 'relative')}>
        <div className="absolute -left-[1000px] top-0 z-[30] h-full w-[1000px] cursor-context-menu bg-white" />
        <Carousel
          options={options}
          className={{
            container: 'w-full gap-4 overflow-visible sm:gap-5 lg:gap-6',
            main: 'w-full overflow-visible',
            wrapper: 'w-full overflow-visible',
            slide: 'w-full max-w-[260px] sm:max-w-[265px] md:max-w-[320px] lg:max-w-[378px]',
          }}
          slides={members.map(member => (
            <MemberCard key={member.id} member={member} />
          ))}
        />
      </div>
      <div className={cn('custom-container', 'pr-4 sm:pr-0')}>
        <CarouselControls
          buttonProps={{ variant: 'primary-foreground', size: 'icon' }}
          className={{
            main: 'hidden gap-4 sm:flex',
            button: cn(
              'border-x-0 border-b-2 border-t-0 border-b-primary bg-primary-foreground text-black hover:bg-primary-foreground/90',
              'ea-services__carousel-control-button',
            ),
          }}
        />
        <div className="flex h-[2px] w-full items-center justify-start bg-black/10 sm:hidden">
          <div
            className={cn(
              'h-1 bg-black transition-all duration-300',
              'ea-team-members__carousel-progress-bar-indicator',
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </section>
  );
}
